import {
  useState, useEffect, useRef, ChangeEvent, KeyboardEvent, SyntheticEvent,
} from 'react';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { parseBarcode } from '#common/utils';
import { KEY_CODES } from '#utils/constants';

type ParsedBarcode = NonNullable<ReturnType<typeof parseBarcode>>;

interface BarcodeState {
  text: string;
  error?: boolean;
  errorText?: string;
  errorColor?: string;
  parsed?: ParsedBarcode | null;
}

interface BarcodeInputBoxProps {
  barcodes?: Array<string | number | null>;
  onChange?: (event: ChangeEvent<HTMLInputElement>, parsed: ParsedBarcode | null) => void;
  onSubmit?: (event: unknown, parsed: ParsedBarcode) => void;
  autoSubmitDelay?: number;
}

export const getDefaultBarcode = () => ({
  // text: '',
  // error: false,
  // errorText: '',
  // errorColor: '',
  // parsed: null,
  barcode: null,
  bulkOrderId: null,
  itemId: null,
  itemIndex: null,
  orderId: null,
  productId: null,
  source: null,
});

export default function BarcodeInputBox(props: BarcodeInputBoxProps) {
  const {
    barcodes,
    onChange,
    onSubmit,
    autoSubmitDelay = 500, // Auto-submit after 500ms of no input
  } = props;

  const [barcodeProps, setBarcodeProps] = useState<BarcodeState>({
    text: '',
    error: false,
    errorText: '',
    errorColor: '',
    parsed: null,
  });

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastInputTimeRef = useRef(0);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = (
    event: SyntheticEvent | null,
    parsedData: ParsedBarcode | null | undefined = barcodeProps.parsed,
  ) => {
    if (barcodeProps.error || !parsedData) {
      return;
    }

    if (barcodes && barcodes.includes(parsedData.barcode as string | number | null)) {
      // duplicated
      setBarcodeProps({
        ...barcodeProps,
        error: false,
        errorColor: 'warning',
        errorText: 'Barcode duplicated',
      });
      return;
    }

    // Create a synthetic event if none provided (for auto-submit)
    const syntheticEvent = event || {
      target: { value: barcodeProps.text },
      preventDefault: () => {},
      type: 'auto-submit',
    };

    if (onSubmit) {
      onSubmit(syntheticEvent, parsedData);
    }

    // Clear the input after successful submission
    setBarcodeProps({
      text: '',
      error: false,
      errorText: '',
      errorColor: '',
      parsed: null,
    });
  };

  const scheduleAutoSubmit = (parsed: ParsedBarcode) => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Schedule new auto-submit if we have valid parsed data
    if (parsed && !barcodeProps.error) {
      timeoutRef.current = setTimeout(() => {
        handleSubmit(null, parsed);
      }, autoSubmitDelay);
    }
  };

  return (
    <TextField
      error={barcodeProps.error}
      helperText={barcodeProps.errorText}
      color={barcodeProps.errorColor as TextFieldProps['color']}
      value={barcodeProps.text}
      slotProps={{
        input: {
          sx: { p: 5 },
        },
        htmlInput: {
          autoComplete: 'off',
          placeholder: 'Input Barcode Here',
          sx: {
            textAlign: 'center',
            fontSize: 40,
          },
        },
      }}
      fullWidth
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        const newText = event.target.value;
        const currentTime = Date.now();

        // Update last input time
        lastInputTimeRef.current = currentTime;

        const newBarcodeProps: BarcodeState = {
          text: newText,
        };

        const parsed = parseBarcode(newText);
        if (!newText) {
          newBarcodeProps.error = false;
          newBarcodeProps.errorColor = '';
          // Clear timeout when input is empty
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
        } else if (parsed) {
          newBarcodeProps.error = false;
          newBarcodeProps.errorColor = 'success';
          newBarcodeProps.parsed = parsed;
          // Schedule auto-submit for valid barcodes
          scheduleAutoSubmit(parsed);
        } else {
          newBarcodeProps.error = true;
          newBarcodeProps.errorColor = '';
          newBarcodeProps.errorText = 'Wrong Barcode';
          // Clear timeout for invalid barcodes
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
        }

        setBarcodeProps(newBarcodeProps);
        if (onChange) {
          onChange(event, parsed);
        }
      }}
      onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === KEY_CODES.ENTER) {
          // Clear any pending auto-submit timeout
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }

          event.preventDefault();
          handleSubmit(event);
        }
      }}
    />
  );
}
